import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { formatDate } from '@/lib/utils'
import { useLicenseStore, type LicensePrompt } from '@/store/license'
import type { BillingPeriod, PlanKey, PlanResponse, SubscriptionResponse } from '@/types/api'

import { PeriodToggle, PlanRow, RECOMMENDED_PLAN, activePlanKey, formatCount, planAction } from './planUi'
import { useCouponStore } from './coupon'
import { CouponField } from './CouponField'
import { SUBSCRIPTION_KEY, hasPaidPlan, useBillingPortal, useCheckout, usePlans, useSubscription } from './useLicense'

/**
 * The upgrade prompt, mounted once in the dashboard shell. It opens on any
 * 402 from the API (store/license.ts) and when an Apply button finds the
 * cached subscription says no (useLicense().requireApply).
 */
export function UpgradeDialog() {
  const prompt = useLicenseStore((state) => state.prompt)
  const seen = useLicenseStore((state) => state.seen)
  const close = useLicenseStore((state) => state.close)
  const queryClient = useQueryClient()

  // Whatever the server just refused, the cached plan and usage are now stale.
  useEffect(() => {
    if (seen > 0) void queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
  }, [seen, queryClient])

  if (!prompt) return null
  return <DialogBody prompt={prompt} onClose={close} />
}

function DialogBody({ prompt, onClose }: { prompt: LicensePrompt; onClose: () => void }) {
  const navigate = useNavigate()
  const plansQuery = usePlans()
  const { data: subscription } = useSubscription()
  const checkout = useCheckout()
  const portal = useBillingPortal()
  const [period, setPeriod] = useState<BillingPeriod>(subscription?.subscription?.period ?? 'monthly')
  const coupon = useCouponStore((state) => state.applied)
  const applyStored = useCouponStore((state) => state.applyStored)
  const paidPlan = hasPaidPlan(subscription)
  useEffect(() => applyStored(), [applyStored])

  const plans = plansQuery.data
  const copy = describe(prompt, subscription ?? null, plans?.plans ?? [])
  const recommended = recommendFor(prompt, subscription ?? null, plans?.plans ?? [])
  const current = activePlanKey(subscription)
  const canFixPayment =
    prompt.reason === 'subscription_inactive' &&
    subscription?.subscription?.status === 'past_due' &&
    subscription.subscription.source === 'stripe'

  const seePlans = () => {
    onClose()
    navigate('/dashboard/plans')
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={copy.title}
      description={copy.description}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Not now
          </Button>
          {canFixPayment && (
            <Button variant="secondary" onClick={portal.open} loading={portal.pending}>
              Update payment
            </Button>
          )}
          <Button variant={canFixPayment ? 'ghost' : 'secondary'} onClick={seePlans}>
            See plans
          </Button>
        </>
      }
    >
      <div className="space-y-3 pb-4">
        {plans ? (
          <>
            <PeriodToggle data={plans} value={period} onChange={setPeriod} size="sm" />
            <ul className="panel divide-y divide-line overflow-hidden" aria-label="Plans">
              {plans.plans.map((plan) => (
                <PlanRow
                  key={plan.key}
                  plan={plan}
                  period={period}
                  current={current === plan.key}
                  recommended={plan.key === recommended}
                  action={planAction({ plan, period, subscription, billingEnabled: plans.billingEnabled })}
                  pending={checkout.pendingPlan === plan.key}
                  onChoose={() => checkout.choose(plan.key, period)}
                  coupon={paidPlan ? null : coupon}
                />
              ))}
            </ul>
            {plans.billingEnabled && <CouponField paidPlan={paidPlan} compact />}
            {!plans.billingEnabled && (
              <p className="text-[12.5px] font-medium text-warning" role="status">
                Payments open soon.
              </p>
            )}
          </>
        ) : plansQuery.isError ? (
          <p className="text-[13.5px] text-ink-muted">The plans could not be loaded. Open the plans page to try again.</p>
        ) : (
          <div className="space-y-2" aria-busy="true">
            {[0, 1, 2].map((index) => (
              <div key={index} className="skeleton h-14 rounded-field" />
            ))}
          </div>
        )}
        <p className="text-[12.5px] leading-relaxed text-ink-faint">
          Without a plan you can still build your profile, see your matches and search with the assistant.
        </p>
      </div>
    </Modal>
  )
}

/** The dialog's title and sentence for each reason. */
function describe(
  prompt: LicensePrompt,
  data: SubscriptionResponse | null,
  plans: PlanResponse[],
): { title: string; description: string } {
  const sub = data?.subscription
  switch (prompt.reason) {
    case 'quota_exhausted': {
      const limit = prompt.limit ?? data?.usage?.limit ?? plans.find((p) => p.key === (prompt.plan ?? sub?.plan))?.monthlyApplications
      const resets = prompt.windowEnd ?? data?.usage?.windowEnd
      const onTop = (prompt.plan ?? sub?.plan) === 'power'
      return {
        title: limit ? `You've used all ${formatCount(limit)} applications this month` : "You've used all your applications this month",
        description: `${resets ? `Your allowance resets on ${formatDate(resets)}.` : 'Your allowance resets next month.'}${onTop ? '' : ' Upgrade for more.'}`,
      }
    }
    case 'feature_not_in_plan':
      if (prompt.feature === 'resumeTailoring' || prompt.feature === 'coverLetterTailoring') {
        return {
          title: 'Tailoring is part of Pro and Power',
          description: 'Pro and Power rewrite your resume and cover letter for every job.',
        }
      }
      return {
        title: 'Auto Apply is part of Power',
        description: 'Power applies for you every day, on the days and hours you choose, and emails you a report each evening.',
      }
    case 'subscription_inactive':
      if (sub?.status === 'past_due' || sub?.status === 'incomplete') {
        return {
          title: 'Your payment failed',
          description: 'Update your card to keep applying, or choose a plan again.',
        }
      }
      return {
        title: 'Your subscription has ended',
        description: sub ? `${sub.planName} ended on ${formatDate(sub.currentPeriodEnd)}. Renew to keep applying.` : 'Renew to keep applying.',
      }
    default:
      return {
        title: 'Choose a plan to start applying',
        description: prompt.detail && prompt.detail.length < 160 ? prompt.detail : 'Pick a plan and we start sending applications for you.',
      }
  }
}

/** The plan to point at: the one that solves this refusal. */
function recommendFor(prompt: LicensePrompt, data: SubscriptionResponse | null, plans: PlanResponse[]): PlanKey {
  if (prompt.reason === 'feature_not_in_plan') {
    const feature = prompt.feature
    if (feature) return plans.find((plan) => plan.features[feature])?.key ?? RECOMMENDED_PLAN
    return RECOMMENDED_PLAN
  }
  if (prompt.reason === 'quota_exhausted') {
    const currentKey = prompt.plan ?? data?.subscription?.plan
    const current = plans.find((plan) => plan.key === currentKey)
    const bigger = plans.find((plan) => current && plan.monthlyApplications > current.monthlyApplications)
    return bigger?.key ?? RECOMMENDED_PLAN
  }
  if (prompt.reason === 'subscription_inactive' && data?.subscription) return data.subscription.plan
  return RECOMMENDED_PLAN
}
